import { useCallback } from 'react';
import { ExecuteStepInput, Reference } from 'sr-types/lib/workflow/v1/graphql';
import { FETCH_POLICY_CONFIG } from '../../constants/constants';
import { useMutationWithContext } from '../../hooks/useMutationWithContext';
import {
    ApprovalActionInputActionMissingError,
    ApprovalActionInputStepIdMissingError,
    ExecuteApprovalMutationError,
    ExecuteApprovalServerError
} from '../constants';
import { executeWorkflowStepMutation, workflowGraphqlClient } from './workflowGraphqlApi';

export function useExecuteApprovalActionApi() {
    const [executeStep, { loading: isExecutingApprovalAction }] = useMutationWithContext(
        executeWorkflowStepMutation,
        workflowGraphqlClient
    );

    const executeApprovalAction = useCallback(
        async (approvalId: string, input: ExecuteStepInput): Promise<Reference | null> => {
            if (!input.stepId?.value) {
                throw new ApprovalActionInputStepIdMissingError();
            }

            /**
             * @todo:
             * Fix the "action" form field handling logic in `ApprovalAction` component
             * to send the "action" value as a number directly, to better align with the
             * `ExecuteStepInput` type definition.
             *
             * Once fixed, this type assertion, extra validation and data override
             * logic can be removed.
             */
            if (!input.action || typeof (input.action as unknown as { id: number }).id !== 'number') {
                throw new ApprovalActionInputActionMissingError();
            }
            input.action = (input.action as unknown as { id: number }).id;

            const mutationFetchResult = await executeStep({
                variables: { workflowId: approvalId, input },
                fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                errorPolicy: 'all'
            });

            if (mutationFetchResult.errors?.length > 0) {
                throw new ExecuteApprovalMutationError(mutationFetchResult.errors[0].message);
            }

            if (mutationFetchResult.data?.executeStep?.errors?.length > 0) {
                throw new ExecuteApprovalServerError(mutationFetchResult.data.executeStep.errors[0].message);
            }

            return mutationFetchResult.data?.executeStep?.reference ?? null;
        },
        [executeStep]
    );

    return {
        executeApprovalAction,
        isExecutingApprovalAction
    };
}
